import { Fragment, useEffect, useRef, useState, type ChangeEventHandler, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import MuiTableContainer from '@mui/material/TableContainer';
import { alpha, type SxProps, type Theme } from '@mui/material/styles';
import type { SortDirection } from '@/shared/hooks/useTableSort';
import { table as tableTokens } from '@/shared/theme/tokens';
import IconButton from '../primitives/IconButton';
import Paper from '../primitives/Paper';
import { mergeSx } from '../utils/mergeSx';
import SortableHeaderCell from './SortableHeaderCell';
import { Table, TableBody, TableCell, TableHead, TableRow } from './Table';
import { LIST_ROWS_PER_PAGE_OPTIONS } from './listPagination';
import TablePagination from './TablePagination';
import TableRowsSkeleton from './TableRowsSkeleton';

/**
 * Single column contract for every list-page table — simple lists and ERP
 * lists (column picker / server sort) share this shape. Optional fields are
 * ignored when unused.
 */
export interface ListColumnDef<T> {
  id: string;
  label?: ReactNode;
  /**
   * Column width in px (required). Used as the share when the table grows
   * and as the floor when it shrinks. Do not omit a column to absorb leftover space.
   */
  width?: number | string;
  /** Extra floor; defaults to `width` when `width` is a number. */
  minWidth?: number | string;
  /**
   * Clip overflowing text with an ellipsis. Defaults to false (text may wrap).
   * Set true for dense single-line columns under `table-layout: fixed`.
   * Ignored on the body when `lineClamp` is set.
   */
  truncate?: boolean;
  /**
   * Wrap body text and ellipsize only after this many lines.
   * Header stays single-line. Do not combine with `truncate` on the same column.
   */
  lineClamp?: number;
  align?: 'left' | 'center' | 'right';
  /**
   * Vertical alignment of the body cell. Line-clamp columns default to top
   * so a short cell lines up with taller neighbors; set `middle` to center
   * a two-line stack in a taller row.
   */
  verticalAlign?: 'top' | 'middle';
  /**
   * Pin the column while the table scrolls horizontally. Multiple `left` /
   * `right` pins stack — offset = sum of widths of earlier pins on that side
   * in the visible column order.
   */
  sticky?: 'left' | 'right';
  /** Always visible; excluded from the column layout drawer. */
  locked?: boolean;
  /** Shown in the column layout drawer when not locked (default true if unlocked). */
  hideable?: boolean;
  /** Initial visibility when hideable (default true). */
  defaultVisible?: boolean;
  sortable?: boolean;
  /** Backend sort field; defaults to `id` when sortable. */
  sortField?: string;
  render: (row: T) => ReactNode;
  /**
   * Totals row pinned under the header. Rendered only when at least one
   * visible column sets this. Empty columns stay blank.
   */
  summary?: ReactNode;
}

/** Clip long cell content so `table-layout: fixed` widths cannot paint over neighbors. */
const cellTruncateSx = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
} as const satisfies SxProps<Theme>;

/** Keep the cell width fixed so the inner line-clamp box cannot grow the column. */
const cellLineClampSx = {
  overflow: 'hidden',
  whiteSpace: 'normal',
  verticalAlign: 'top',
} as const satisfies SxProps<Theme>;

function lineClampSx(lines: number): SxProps<Theme> {
  return {
    display: '-webkit-box',
    width: '100%',
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: lines,
    overflow: 'hidden',
    whiteSpace: 'normal',
    overflowWrap: 'break-word',
  };
}

const numericWidth = (width: number | string | undefined): number =>
  typeof width === 'number' ? width : 0;

function computeStickyOffsets<T>(columns: readonly ListColumnDef<T>[]): Map<string, number> {
  const offsets = new Map<string, number>();
  let leftAcc = 0;
  for (const column of columns) {
    if (column.sticky === 'left') {
      offsets.set(column.id, leftAcc);
      leftAcc += numericWidth(column.width);
    }
  }
  let rightAcc = 0;
  for (let index = columns.length - 1; index >= 0; index -= 1) {
    const column = columns[index];
    if (column.sticky === 'right') {
      offsets.set(column.id, rightAcc);
      rightAcc += numericWidth(column.width);
    }
  }
  return offsets;
}

function columnCellSx<T>(
  column: ListColumnDef<T>,
  variant: 'head' | 'body',
  stickyOffsetPx = 0,
): SxProps<Theme> {
  const lineClamp = variant === 'body' && column.lineClamp !== undefined && column.lineClamp > 0;
  const truncate = !lineClamp && column.truncate === true;
  const widthFloor =
    column.minWidth ?? (typeof column.width === 'number' ? column.width : undefined);
  const widthSx: SxProps<Theme> = {
    ...(lineClamp ? cellLineClampSx : {}),
    ...(truncate ? cellTruncateSx : {}),
    ...(column.verticalAlign ? { verticalAlign: column.verticalAlign } : {}),
    ...(column.width !== undefined ? { width: column.width } : {}),
    ...(widthFloor !== undefined ? { minWidth: widthFloor } : {}),
  };

  if (!column.sticky) return widthSx;

  const stackRank = stickyOffsetPx > 0 ? Math.min(Math.floor(stickyOffsetPx / 40), 8) : 0;
  return {
    ...widthSx,
    position: 'sticky',
    [column.sticky]: stickyOffsetPx,
    // Head cells sit above stickyHeader (z-index 2); body above scrolling cells.
    zIndex: (variant === 'head' ? 3 : 1) + stackRank,
    bgcolor: 'background.paper',
    ...(variant === 'body'
      ? {
          // Overlay on opaque paper — a translucent bgcolor shows the page
          // through the cell, so sticky columns look a different grey.
          'tr:hover:not(.Mui-selected) > &': {
            backgroundImage: (theme) =>
              `linear-gradient(${theme.palette.action.hover}, ${theme.palette.action.hover})`,
          },
          'tr.Mui-selected > &': {
            backgroundImage: (theme) => {
              const color = alpha(
                theme.palette.primary.main,
                theme.palette.action.selectedOpacity
              );
              return `linear-gradient(${color}, ${color})`;
            },
          },
          'tr.Mui-selected:hover > &': {
            backgroundImage: (theme) => {
              const color = alpha(
                theme.palette.primary.main,
                theme.palette.action.selectedOpacity + theme.palette.action.hoverOpacity
              );
              return `linear-gradient(${color}, ${color})`;
            },
          },
        }
      : {}),
  };
}

/** @deprecated Use `ListColumnDef` — kept as an alias during migration. */
export type ListTableColumn<T> = ListColumnDef<T>;

export interface ListTablePagination {
  count: number;
  page: number;
  rowsPerPage: number;
  onPageChange: (event: unknown, page: number) => void;
  onRowsPerPageChange?: ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  /** Defaults to `LIST_ROWS_PER_PAGE_OPTIONS` (10, 25, 50, 100). */
  rowsPerPageOptions?: number[];
}

export interface ListTableSort {
  field: string;
  direction: SortDirection;
  onToggle: (field: string) => void;
  getDirection: (field: string) => SortDirection | false;
}

export interface ListTableRowGroup {
  id: string;
  label: ReactNode;
}

export interface ListTableProps<T> {
  columns: ListColumnDef<T>[];
  rows: T[];
  getRowId: (row: T) => string | number;
  /**
   * When set (from `useTableColumns.visibleIds`), only these columns render.
   * Omit to show every column.
   */
  visibleIds?: Set<string>;
  /**
   * Display order from `useTableColumns.orderedIds`. Ids missing from this
   * list keep their relative position at the end.
   */
  columnOrder?: readonly string[];
  /**
   * Background refetch: keeps head/footer mounted and swaps only the body
   * rows for skeletons (pair with React Query's `isFetching && !isPending`).
   */
  refreshing?: boolean;
  /**
   * Skeleton row height in px, matching real rows, to avoid vertical layout
   * shift. Default 46 = 8px cell padding × 2 + a small icon button (30px).
   */
  skeletonRowHeight?: number;
  /** Shown as a single centered row when there are no rows. */
  emptyContent?: ReactNode;
  pagination?: ListTablePagination;
  /** Wire from `useTableSort` when any column is `sortable`. */
  sort?: ListTableSort;
  /**
   * Insert a section row whenever this id changes. Rows must already be
   * ordered by the group, including across pages. Child rows start hidden
   * and the section header toggles them. Data rows in the first column are
   * indented under that section.
   */
  rowGroup?: (row: T) => ListTableRowGroup;
  /**
   * Totals on the group header row, keyed by column id. The label merges
   * leading empty columns so the title is not trapped in the first column
   * width; remaining columns keep their own total cells.
   */
  groupCells?: (groupId: string) => Partial<Record<string, ReactNode>> | undefined;
  onRowClick?: (row: T) => void;
  /**
   * When set, prepends an expand column and renders a detail row under the
   * master. Pass `expandedRowId` + `onExpandedRowChange` for controlled mode;
   * omit them for internal accordion state (one row open at a time).
   */
  renderExpandedRow?: (row: T) => ReactNode;
  expandedRowId?: string | number | null;
  onExpandedRowChange?: (id: string | number | null) => void;
  /** Paper shadow. Omit for the list-page default (2). Use 0 on a surface that already has an edge. */
  elevation?: number;
  sx?: SxProps<Theme>;
}

const EXPAND_COL_WIDTH = 40;

function orderColumns<T>(
  columns: ListColumnDef<T>[],
  columnOrder: readonly string[],
): ListColumnDef<T>[] {
  const rank = new Map(columnOrder.map((id, index) => [id, index]));
  return [...columns].sort((left, right) => {
    const leftRank = rank.get(left.id);
    const rightRank = rank.get(right.id);
    if (leftRank === undefined && rightRank === undefined) return 0;
    if (leftRank === undefined) return 1;
    if (rightRank === undefined) return -1;
    return leftRank - rightRank;
  });
}

/**
 * Standard table for list pages: every column has a px width, the table
 * grows with the viewport (`width: 100%`) and scrolls horizontally when
 * the container is narrower than the sum of those widths. Sticky header,
 * body-only skeleton on refresh, pagination pinned to the bottom.
 */
const ListTable = <T,>({
  columns,
  rows,
  getRowId,
  visibleIds,
  columnOrder,
  refreshing = false,
  skeletonRowHeight = 46,
  emptyContent,
  pagination,
  sort,
  rowGroup,
  groupCells,
  onRowClick,
  renderExpandedRow,
  expandedRowId: expandedRowIdProp,
  onExpandedRowChange,
  elevation,
  sx,
}: ListTableProps<T>) => {
  const { t } = useTranslation('common');
  const expandable = Boolean(renderExpandedRow);
  const isExpandControlled = expandedRowIdProp !== undefined;
  const [uncontrolledExpandedId, setUncontrolledExpandedId] = useState<
    string | number | null
  >(null);
  const expandedRowId = isExpandControlled ? expandedRowIdProp : uncontrolledExpandedId;
  const [openGroupIds, setOpenGroupIds] = useState<ReadonlySet<string>>(() => new Set());
  const hadRowGroups = useRef(false);
  const grouping = Boolean(rowGroup);

  useEffect(() => {
    if (grouping) {
      hadRowGroups.current = true;
      return;
    }
    if (!hadRowGroups.current) return;
    hadRowGroups.current = false;
    setOpenGroupIds(new Set());
  }, [grouping]);

  const orderedColumns =
    columnOrder && columnOrder.length > 0 ? orderColumns(columns, columnOrder) : columns;
  const visibleColumns =
    visibleIds !== undefined
      ? orderedColumns.filter((column) => visibleIds.has(column.id))
      : orderedColumns;

  const columnCount = visibleColumns.length + (expandable ? 1 : 0);
  const stickyOffsets = computeStickyOffsets(visibleColumns);
  const tableMinWidth =
    visibleColumns.reduce((sum, column) => sum + numericWidth(column.width), 0) +
    (expandable ? EXPAND_COL_WIDTH : 0);

  const setExpandedRowId = (id: string | number | null): void => {
    if (!isExpandControlled) {
      setUncontrolledExpandedId(id);
    }
    onExpandedRowChange?.(id);
  };

  const toggleExpanded = (rowId: string | number): void => {
    setExpandedRowId(expandedRowId === rowId ? null : rowId);
  };

  const footer = pagination ? (
    <TablePagination
      component="div"
      count={pagination.count}
      page={pagination.page}
      onPageChange={pagination.onPageChange}
      rowsPerPage={pagination.rowsPerPage}
      onRowsPerPageChange={pagination.onRowsPerPageChange}
      rowsPerPageOptions={pagination.rowsPerPageOptions ?? LIST_ROWS_PER_PAGE_OPTIONS}
      labelRowsPerPage={t('pagination.rowsPerPage')}
      labelDisplayedRows={({ from, to, count }) =>
        count !== -1
          ? t('pagination.displayedRows', { from, to, count })
          : `${from}–${to} ${t('pagination.moreThan', { to })}`
      }
    />
  ) : null;

  return (
    <Paper
      elevation={elevation}
      sx={mergeSx(
        {
          flex: 1,
          minHeight: 0,
          minWidth: 0,
          width: '100%',
          alignSelf: 'stretch',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          bgcolor: 'background.paper',
        },
        sx,
      )}
    >
      <MuiTableContainer sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
        <Table
          sx={{
            tableLayout: 'fixed',
            width: '100%',
            ...(tableMinWidth > 0 ? { minWidth: tableMinWidth } : {}),
          }}
        >
          <TableHead
            sx={
              visibleColumns.some((column) => column.summary !== undefined)
                ? {
                    '& .MuiTableCell-stickyHeader.ListTable-summary': {
                      top: tableTokens.headerRowHeight,
                      color: 'text.primary',
                      fontWeight: 600,
                      bgcolor: 'grey.50',
                    },
                  }
                : undefined
            }
          >
            <TableRow>
              {expandable && <TableCell sx={{ width: EXPAND_COL_WIDTH }} />}
              {visibleColumns.map((column) => {
                const sortField = column.sortField ?? column.id;
                return (
                  <TableCell
                    key={column.id}
                    align={column.align}
                    sx={columnCellSx(column, 'head', stickyOffsets.get(column.id) ?? 0)}
                  >
                    {column.sortable && sort ? (
                      <SortableHeaderCell
                        label={typeof column.label === 'string' ? column.label : ''}
                        active={sort.field === sortField}
                        direction={sort.getDirection(sortField) || 'asc'}
                        onSort={() => sort.onToggle(sortField)}
                      />
                    ) : (
                      column.label
                    )}
                  </TableCell>
                );
              })}
            </TableRow>
            {visibleColumns.some((column) => column.summary !== undefined) && (
              <TableRow>
                {expandable && <TableCell className="ListTable-summary" sx={{ width: EXPAND_COL_WIDTH }} />}
                {visibleColumns.map((column) => (
                  <TableCell
                    key={column.id}
                    className="ListTable-summary"
                    align={column.align}
                    sx={columnCellSx(column, 'head', stickyOffsets.get(column.id) ?? 0)}
                  >
                    {column.summary}
                  </TableCell>
                ))}
              </TableRow>
            )}
          </TableHead>
          <TableBody>
            {refreshing ? (
              <TableRowsSkeleton
                columns={columnCount}
                rows={Math.min(pagination?.rowsPerPage ?? 10, Math.max(rows.length, 3))}
                rowHeight={skeletonRowHeight}
              />
            ) : rows.length > 0 ? (
              rows.map((row, index) => {
                const rowId = getRowId(row);
                const isExpanded = expandable && expandedRowId === rowId;
                const group = rowGroup?.(row);
                const previousGroup = index > 0 ? rowGroup?.(rows[index - 1]) : undefined;
                const showGroup = Boolean(group && group.id !== previousGroup?.id);
                const groupOpen = !group || openGroupIds.has(group.id);
                const groupedCells = group && showGroup ? groupCells?.(group.id) : undefined;

                return (
                  <Fragment key={rowId}>
                    {showGroup && group ? (
                      <TableRow className="ListTable-group">
                        {expandable && (
                          <TableCell sx={{ width: EXPAND_COL_WIDTH, bgcolor: 'grey.100' }} />
                        )}
                        {(() => {
                          const groupHeaderSx = {
                            fontWeight: 600,
                            bgcolor: 'grey.100',
                            'tr:hover > &': { backgroundImage: 'none' },
                          } as const;
                          // Title merges leading empty columns; totals stay in
                          // their own columns (e.g. cashbook amount).
                          let firstFilledIndex = visibleColumns.length;
                          for (let i = 1; i < visibleColumns.length; i += 1) {
                            if (groupedCells?.[visibleColumns[i].id] != null) {
                              firstFilledIndex = i;
                              break;
                            }
                          }
                          const labelColSpan = Math.max(1, firstFilledIndex);

                          return (
                            <>
                              <TableCell
                                key="group-label"
                                colSpan={labelColSpan}
                                align="left"
                                sx={groupHeaderSx}
                              >
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                    minWidth: 0,
                                  }}
                                >
                                  <IconButton
                                    size="small"
                                    aria-label={
                                      groupOpen
                                        ? t('table.collapseGroup')
                                        : t('table.expandGroup')
                                    }
                                    aria-expanded={groupOpen}
                                    onClick={() => {
                                      const groupId = group.id;
                                      setOpenGroupIds((current) => {
                                        const next = new Set(current);
                                        if (next.has(groupId)) next.delete(groupId);
                                        else next.add(groupId);
                                        return next;
                                      });
                                    }}
                                  >
                                    {groupOpen ? (
                                      <KeyboardArrowDownIcon fontSize="small" />
                                    ) : (
                                      <KeyboardArrowRightIcon fontSize="small" />
                                    )}
                                  </IconButton>
                                  <Box
                                    component="span"
                                    sx={{
                                      minWidth: 0,
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                    }}
                                  >
                                    {group.label}
                                  </Box>
                                </Box>
                              </TableCell>
                              {visibleColumns.slice(firstFilledIndex).map((column) => (
                                <TableCell
                                  key={column.id}
                                  align={column.align}
                                  sx={mergeSx(
                                    columnCellSx(
                                      column,
                                      'body',
                                      stickyOffsets.get(column.id) ?? 0
                                    ),
                                    groupHeaderSx
                                  )}
                                >
                                  {groupedCells?.[column.id]}
                                </TableCell>
                              ))}
                            </>
                          );
                        })()}
                      </TableRow>
                    ) : null}
                    {groupOpen ? (
                    <>
                    <TableRow
                      hover
                      selected={isExpanded}
                      onClick={onRowClick ? () => onRowClick(row) : undefined}
                      sx={onRowClick ? { cursor: 'pointer' } : undefined}
                    >
                      {expandable && (
                        <TableCell sx={{ width: EXPAND_COL_WIDTH, px: 0.5 }}>
                          <IconButton
                            size="small"
                            aria-label={
                              isExpanded
                                ? t('table.collapseRow')
                                : t('table.expandRow')
                            }
                            aria-expanded={isExpanded}
                            onClick={(event) => {
                              event.stopPropagation();
                              toggleExpanded(rowId);
                            }}
                          >
                            {isExpanded ? (
                              <KeyboardArrowUpIcon fontSize="small" />
                            ) : (
                              <KeyboardArrowDownIcon fontSize="small" />
                            )}
                          </IconButton>
                        </TableCell>
                      )}
                      {visibleColumns.map((column, columnIndex) => {
                        const content = column.render(row);
                        const clamp =
                          column.lineClamp !== undefined && column.lineClamp > 0
                            ? column.lineClamp
                            : undefined;
                        const groupedContent =
                          rowGroup && columnIndex === 0 ? (
                            <Box component="span" sx={{ display: 'block', pl: 2, minWidth: 0 }}>
                              {content}
                            </Box>
                          ) : (
                            content
                          );
                        return (
                          <TableCell
                            key={column.id}
                            align={column.align}
                            sx={columnCellSx(column, 'body', stickyOffsets.get(column.id) ?? 0)}
                          >
                            {clamp !== undefined ? (
                              <Box component="span" sx={lineClampSx(clamp)}>
                                {groupedContent}
                              </Box>
                            ) : (
                              groupedContent
                            )}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                    {expandable && (
                      <TableRow sx={isExpanded ? undefined : { height: 0 }}>
                        <TableCell
                          colSpan={columnCount}
                          sx={{
                            // Body padding lives on a :not(head) selector, so py: 0
                            // loses. A closed expand row then leaves a gap above the next row.
                            borderBottom: isExpanded ? undefined : 0,
                            '&&:not(.MuiTableCell-head):not(.MuiTableCell-footer)': {
                              py: 0,
                              ...(isExpanded
                                ? {}
                                : { height: 0, lineHeight: 0, fontSize: 0, borderBottomWidth: 0 }),
                            },
                          }}
                        >
                          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                            <Box sx={{ py: 1.5, px: 1 }}>
                              {renderExpandedRow?.(row)}
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    )}
                    </>
                    ) : null}
                  </Fragment>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={columnCount || 1} align="center">
                  {emptyContent ?? t('noData')}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </MuiTableContainer>
      {footer && (
        <Box
          sx={{
            flexShrink: 0,
            borderTop: 1,
            borderColor: 'divider',
            color: 'text.secondary',
            minHeight: tableTokens.footerMinHeight,
            px: tableTokens.footerPaddingX,
            py: tableTokens.footerPaddingY,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
          }}
        >
          {footer}
        </Box>
      )}
    </Paper>
  );
};

export default ListTable;
